/**
 * Schéma du contenu pédagogique de Néopolis 2040.
 *
 * Règle non négociable n° 3 : ce fichier est la source de vérité de la structure
 * de `content/neopolis-contenu-mvp.json`. Aucune modification de la structure du
 * JSON sans mise à jour du schéma — et sans confirmation préalable.
 *
 * Les identifiants *structurels* (jauges, rôles, catégories d'étapes) sont des
 * énumérations TypeScript : le moteur en a besoin comme types littéraux. Leur
 * cohérence avec le JSON est vérifiée par `checkContentIntegrity`.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------ */
/* Énumérations structurelles                                          */
/* ------------------------------------------------------------------ */

export const GAUGE_IDS = ['budget', 'consommation', 'co2', 'satisfaction', 'confiance'] as const;
export const gaugeIdSchema = z.enum(GAUGE_IDS);
export type GaugeId = z.infer<typeof gaugeIdSchema>;

export const CHAIN_ROLES = ['acquerir', 'traiter', 'communiquer', 'agir'] as const;
export const chainRoleSchema = z.enum(CHAIN_ROLES);
export type ChainRole = z.infer<typeof chainRoleSchema>;

export const CHAPTER_IDS = ['ch1', 'ch2', 'ch3', 'ch4'] as const;
export const chapterIdSchema = z.enum(CHAPTER_IDS);
export type ChapterId = z.infer<typeof chapterIdSchema>;

/** Chapitres hors MVP, cités par `chapitre_solution` et `couverture_sequence`. */
export const FUTURE_CHAPTER_IDS = ['ch5', 'ch6', 'ch7'] as const;
export const anyChapterIdSchema = z.enum([...CHAPTER_IDS, ...FUTURE_CHAPTER_IDS]);
export type AnyChapterId = z.infer<typeof anyChapterIdSchema>;

/**
 * Les comparateurs qu'une condition de programme peut utiliser.
 *
 * Deux suffisent au chapitre 3, et c'est voulu : « SI l'humidité est inférieure
 * à 30 % » se lit à voix haute par un élève de 4e. Une égalité stricte sur une
 * grandeur mesurée n'aurait, elle, presque jamais de sens.
 */
export const COMPARATEURS = ['inferieur', 'superieur'] as const;
export const comparateurSchema = z.enum(COMPARATEURS);
export type Comparateur = z.infer<typeof comparateurSchema>;

export const gaugeDirectionSchema = z.enum(['bas_meilleur', 'haut_meilleur']);
export type GaugeDirection = z.infer<typeof gaugeDirectionSchema>;

export const connectedUsefulnessSchema = z.enum(['oui', 'en_partie', 'non']);
export type ConnectedUsefulness = z.infer<typeof connectedUsefulnessSchema>;

export const solutionQualitySchema = z.enum(['simple', 'correcte', 'optimale', 'surdimensionnee']);
export type SolutionQuality = z.infer<typeof solutionQualitySchema>;

export const mechanicSchema = z.enum([
  'exploration_carte',
  'tri_categories',
  'choix_oui_partiel_non',
  'priorisation_top3',
  'observation_guidee',
  'glisser_deposer_chaine',
  'simulation_nuit',
  'evenement',
  // Chapitre 3 : lire puis corriger un programme, et l'éprouver sur une semaine.
  'reparer_programme',
  'simulation_semaine',
  // Chapitre 4 : choisir les arrêts du camion, puis lire le bilan de la semaine.
  'organiser_tournee',
  'bilan_tournees',
]);
export type Mechanic = z.infer<typeof mechanicSchema>;

export const traceFieldTypeSchema = z.enum(['texte_court', 'texte_long', 'genere_auto']);
export type TraceFieldType = z.infer<typeof traceFieldTypeSchema>;

/* ------------------------------------------------------------------ */
/* Briques communes                                                    */
/* ------------------------------------------------------------------ */

/** Identifiant de contenu : lettres, chiffres et tirets bas. */
const idSchema = z
  .string()
  .min(1)
  .regex(/^[A-Za-z0-9_]+$/, 'Un identifiant ne contient que des lettres, chiffres ou « _ ».');

const nonEmptyText = z.string().min(1);

/**
 * Variation appliquée à une ou plusieurs jauges.
 * Volontairement exhaustif plutôt qu'un `record` : chaque jauge inconnue doit
 * échouer à la validation (`strict`), et le type inféré reste précis.
 */
export const effectsSchema = z
  .object({
    budget: z.number().optional(),
    consommation: z.number().optional(),
    co2: z.number().optional(),
    satisfaction: z.number().optional(),
    confiance: z.number().optional(),
  })
  .strict();
export type Effects = z.infer<typeof effectsSchema>;

/* ------------------------------------------------------------------ */
/* Sections du contenu                                                 */
/* ------------------------------------------------------------------ */

export const metaSchema = z
  .object({
    titre: nonEmptyText,
    version: nonEmptyText,
    niveau: nonEmptyText,
    discipline: nonEmptyText,
    note_programme: nonEmptyText,
    reference_programme: z
      .object({
        texte: nonEmptyText,
        url: z.url(),
        enjeu_cite: nonEmptyText,
      })
      .strict(),
    unite_budget: nonEmptyText,
  })
  .strict();

export const gaugeSchema = z
  .object({
    id: gaugeIdSchema,
    nom: nonEmptyText,
    icone: nonEmptyText,
    valeur_initiale: z.number(),
    min: z.number(),
    max: z.number(),
    sens: gaugeDirectionSchema,
    description: nonEmptyText,
  })
  .strict()
  .refine((g) => g.min <= g.max, {
    message: 'La borne min doit être inférieure ou égale à la borne max.',
  })
  .refine((g) => g.valeur_initiale >= g.min && g.valeur_initiale <= g.max, {
    message: 'La valeur initiale doit être comprise entre min et max.',
  });
export type Gauge = z.infer<typeof gaugeSchema>;

export const districtSchema = z
  .object({
    id: idSchema,
    nom: nonEmptyText,
    description: nonEmptyText,
  })
  .strict();
export type District = z.infer<typeof districtSchema>;

export const problemCategorySchema = z
  .object({
    id: idSchema,
    nom: nonEmptyText,
    couleur: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Couleur hexadécimale attendue, ex. #F5B700.'),
  })
  .strict();
export type ProblemCategory = z.infer<typeof problemCategorySchema>;

export const competenceRefSchema = z
  .object({
    id: idSchema,
    attendu_4e: nonEmptyText,
    a_verifier: z.boolean(),
  })
  .strict();

export const stepSchema = z
  .object({
    etape: z.number().int().positive(),
    titre: nonEmptyText,
    consigne: nonEmptyText,
    mecanique: mechanicSchema,
    problemes_cibles: z.array(idSchema).optional(),
  })
  .strict();
export type Step = z.infer<typeof stepSchema>;

export const traceFieldSchema = z
  .object({
    id: idSchema,
    label: nonEmptyText,
    type: traceFieldTypeSchema,
    reponse_attendue: nonEmptyText.optional(),
  })
  .strict();
export type TraceField = z.infer<typeof traceFieldSchema>;

export const writtenTraceSchema = z
  .object({
    titre: nonEmptyText,
    champs: z.array(traceFieldSchema).min(1),
    a_retenir: nonEmptyText,
  })
  .strict();
export type WrittenTrace = z.infer<typeof writtenTraceSchema>;

export const chainSlotSchema = z
  .object({
    id: chainRoleSchema,
    nom: nonEmptyText,
    question: nonEmptyText,
    obligatoire: z.boolean(),
    max: z.number().int().positive(),
  })
  .strict();
export type ChainSlot = z.infer<typeof chainSlotSchema>;

export const guidedChoiceSchema = z
  .object({
    id: idSchema,
    texte: nonEmptyText,
    correct: z.boolean(),
  })
  .strict();
export type GuidedChoice = z.infer<typeof guidedChoiceSchema>;

/**
 * Question guidée de l'étape « Comprendre » (chapitre 2).
 *
 * Ajoutée au schéma le 2026-09-26, avec l'accord de l'enseignant : le plan la
 * prévoyait explicitement (« avec les réponses dans le JSON, à ajouter au
 * schéma, avec confirmation »).
 */
export const guidedQuestionSchema = z
  .object({
    id: idSchema,
    question: nonEmptyText,
    choix: z.array(guidedChoiceSchema).min(2),
    explication: nonEmptyText,
  })
  .strict()
  .refine((q) => q.choix.filter((choix) => choix.correct).length === 1, {
    message: 'Une question guidée doit avoir exactement une bonne réponse.',
  });
export type GuidedQuestion = z.infer<typeof guidedQuestionSchema>;

export const chapter1ScoreSchema = z
  .object({
    bonne_categorie: z.number(),
    bonne_analyse_connecte: z.number(),
    priorite_alignee_habitants: z.number(),
  })
  .strict();
export type Chapter1Score = z.infer<typeof chapter1ScoreSchema>;

export const chapterSchema = z
  .object({
    id: chapterIdSchema,
    titre: nonEmptyText,
    duree_min: z.number().int().positive(),
    situation_depart: nonEmptyText,
    objectifs: z.array(nonEmptyText).min(1),
    competences: z.array(competenceRefSchema).min(1),
    deroule: z.array(stepSchema).min(1),
    cases_chaine: z.array(chainSlotSchema).optional(),
    questions_guidees: z.array(guidedQuestionSchema).min(1).optional(),
    trace_ecrite: writtenTraceSchema,
    score: chapter1ScoreSchema.optional(),
  })
  .strict();
export type Chapter = z.infer<typeof chapterSchema>;

export const clueSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('temoignage'), auteur: nonEmptyText, texte: nonEmptyText }).strict(),
  z.object({ type: z.literal('donnee'), texte: nonEmptyText }).strict(),
]);
export type Clue = z.infer<typeof clueSchema>;

export const problemSchema = z
  .object({
    id: idSchema,
    titre: nonEmptyText,
    quartier: idSchema,
    categorie: idSchema,
    indices: z.array(clueSchema).min(1),
    objet_connecte_utile: connectedUsefulnessSchema,
    explication: nonEmptyText,
    priorite_habitants: z.number().int().min(1).max(3),
    chapitre_solution: anyChapterIdSchema.nullable(),
  })
  .strict();
export type Problem = z.infer<typeof problemSchema>;

export const equipmentSchema = z
  .object({
    id: idSchema,
    nom: nonEmptyText,
    icone: nonEmptyText,
    role: chainRoleSchema,
    mesure: nonEmptyText.nullable().optional(),
    description: nonEmptyText.optional(),
    cout: z.number().nonnegative(),
    impact_fabrication: effectsSchema,
    effet_permanent: effectsSchema.optional(),
    usages: z.array(idSchema),
    message_inadapte: nonEmptyText.nullable(),
  })
  .strict();
export type Equipment = z.infer<typeof equipmentSchema>;

export const chainCombinationSchema = z
  .object({
    acquerir: z.array(idSchema),
    traiter: z.array(idSchema),
    communiquer: z.array(idSchema),
    agir: z.array(idSchema),
  })
  .strict();
export type ChainCombination = z.infer<typeof chainCombinationSchema>;

export const solutionSchema = z
  .object({
    id: idSchema,
    probleme: idSchema,
    combinaison: chainCombinationSchema,
    qualite: solutionQualitySchema,
    etoiles: z.number().int().min(1).max(3),
    effets_usage: effectsSchema,
    feedback: nonEmptyText,
    note_moteur: nonEmptyText.optional(),
  })
  .strict();
export type Solution = z.infer<typeof solutionSchema>;

/* ------------------------------------------------------------------ */
/* Chapitre 3 — réparer un programme                                   */
/* ------------------------------------------------------------------ */

/**
 * Une condition « SI <grandeur> <comparateur> <seuil> ».
 *
 * `null` veut dire qu'il n'y en a pas — c'est l'état du programme de la ville
 * au départ : il arrose sans rien regarder.
 */
export const conditionSchema = z
  .object({
    grandeur: idSchema,
    comparateur: comparateurSchema,
    seuil: z.number(),
  })
  .strict();
export type Condition = z.infer<typeof conditionSchema>;

/** L'état d'un programme : une heure, une durée, et une condition ou non. */
export const reglageProgrammeSchema = z
  .object({
    heure: z.number().int().min(0).max(23),
    duree_minutes: z.number().int().min(0),
    condition: conditionSchema.nullable(),
  })
  .strict();
export type ReglageProgramme = z.infer<typeof reglageProgrammeSchema>;

/** Une grandeur que l'îlot peut faire lire au programme, et le capteur qui la mesure. */
export const grandeurSchema = z
  .object({
    id: idSchema,
    nom: nonEmptyText,
    unite: nonEmptyText,
    /** Le capteur correspondant, pour rattacher le programme à la chaîne du chapitre 2. */
    equipement: idSchema,
  })
  .strict();
export type Grandeur = z.infer<typeof grandeurSchema>;

/**
 * Ce que l'îlot a le droit de changer.
 *
 * Des listes fermées, pas des champs libres : on veut que l'élève raisonne sur
 * « le soir plutôt que midi », pas qu'il cherche la minute exacte.
 */
export const comparateurLisibleSchema = z
  .object({
    id: comparateurSchema,
    /** Comment la condition se lit à voix haute : « est inférieure à ». */
    nom: nonEmptyText,
    /** Le symbole, pour l'écran : « < ». */
    symbole: nonEmptyText,
  })
  .strict();
export type ComparateurLisible = z.infer<typeof comparateurLisibleSchema>;

export const reglagesDisponiblesSchema = z
  .object({
    heures: z.array(z.number().int().min(0).max(23)).min(2),
    durees_minutes: z.array(z.number().int().min(0)).min(2),
    grandeurs: z.array(grandeurSchema).min(1),
    comparateurs: z.array(comparateurLisibleSchema).min(1),
    seuils: z.array(z.number()).min(2),
  })
  .strict();

/**
 * Une façon de régler le programme, et ce qu'elle vaut.
 *
 * Même grammaire que `solutions` — `qualite`, `etoiles`, `effets_usage`,
 * `feedback` — parce que c'est la même idée : plusieurs réponses tiennent
 * debout, toutes ne se valent pas. Arroser à 6 h du matin n'est pas faux, c'est
 * seulement moins bon que le soir.
 */
export const varianteProgrammeSchema = z
  .object({
    id: idSchema,
    reglage: reglageProgrammeSchema,
    qualite: solutionQualitySchema,
    etoiles: z.number().int().min(1).max(3),
    effets_usage: effectsSchema,
    feedback: nonEmptyText,
  })
  .strict();
export type VarianteProgramme = z.infer<typeof varianteProgrammeSchema>;

/** Les constantes de la simulation d'une semaine. Règle n° 2 : les chiffres viennent du contenu. */
export const simulationArrosageSchema = z
  .object({
    litres_par_minute: z.number().positive(),
    /** En dessous, les plantes souffrent. */
    humidite_min_plantes: z.number().min(0).max(100),
    gain_humidite_par_minute: z.number().positive(),
    /** Ce que le sol perd dans la journée, selon qu'il fait chaud ou non. */
    perte_si_chaud: z.number().min(0),
    perte_sinon: z.number().min(0),
    seuil_chaud_celsius: z.number(),
    /**
     * Part de l'eau perdue quand on arrose en pleine journée. C'est le cœur de
     * la leçon : l'eau est payée, mais elle n'arrive pas aux racines.
     */
    evaporation_en_journee: z.number().min(0).max(1),
    /**
     * Part perdue quand on arrose tôt le matin.
     *
     * Plus faible qu'à midi — il fait frais — mais pas nulle : l'eau reste en
     * surface pendant la journée de chaleur qui suit, alors qu'un arrosage du
     * soir s'infiltre toute la nuit. C'est ce qui sépare deux étoiles de trois.
     */
    evaporation_le_matin: z.number().min(0).max(1),
    /** Heures à partir desquelles l'arrosage ne s'évapore plus. */
    heure_fraiche_soir: z.number().int().min(0).max(23),
    heure_fraiche_matin: z.number().int().min(0).max(23),
  })
  .strict();

export const programmeSchema = z
  .object({
    id: idSchema,
    probleme: idSchema,
    chapitre: chapterIdSchema,
    titre: nonEmptyText,
    /** Le programme que la ville utilise aujourd'hui : celui qu'on donne à lire. */
    depart: reglageProgrammeSchema,
    reglages: reglagesDisponiblesSchema,
    variantes: z.array(varianteProgrammeSchema).min(1),
    /** Ce qu'on dit quand le réglage ne correspond à aucune variante listée. */
    feedback_inconnu: nonEmptyText,
    simulation: simulationArrosageSchema,
  })
  .strict();
export type Programme = z.infer<typeof programmeSchema>;

/** Une journée de la semaine simulée. */
export const journeeMeteoSchema = z
  .object({
    jour: nonEmptyText,
    pluie_mm: z.number().min(0),
    temperature: z.number(),
    humidite_sol_matin: z.number().min(0).max(100),
  })
  .strict();
export type JourneeMeteo = z.infer<typeof journeeMeteoSchema>;

/* ------------------------------------------------------------------ */
/* Chapitre 4 — organiser la collecte des déchets                      */
/* ------------------------------------------------------------------ */

/**
 * Un conteneur à collecter.
 *
 * `km_detour` est ce que son passage ajoute à la boucle du camion : le modèle
 * de distance est volontairement simple, pour qu'un élève de 4e puisse le
 * refaire de tête. Le camion suit une boucle, chaque arrêt retenu l'allonge.
 */
export const conteneurSchema = z
  .object({
    id: idSchema,
    nom: nonEmptyText,
    quartier: idSchema,
    km_detour: z.number().min(0),
    /** Taux de remplissage au lundi matin, en pourcentage. */
    remplissage_initial: z.number().min(0).max(100),
    /** Ce que le conteneur gagne chaque jour, en points de pourcentage. */
    vitesse_par_jour: z.number().min(0).max(100),
  })
  .strict();
export type Conteneur = z.infer<typeof conteneurSchema>;

/** Le camion-benne, et ce que ses kilomètres coûtent à l'environnement. */
export const camionSchema = z
  .object({
    /** Distance parcourue même sans aucun arrêt : sortir le camion coûte déjà. */
    boucle_km_base: z.number().min(0),
    litres_aux_cent: z.number().positive(),
    kg_co2_par_litre: z.number().positive(),
  })
  .strict();

/** La tournée telle qu'elle se fait aujourd'hui, pour la comparaison. */
export const tourneeActuelleSchema = z
  .object({
    km_par_sortie: z.number().min(0),
    sorties_par_semaine: z.number().int().min(1).max(7),
    description: nonEmptyText,
  })
  .strict();

export const tourneeSchema = z
  .object({
    id: idSchema,
    probleme: idSchema,
    chapitre: chapterIdSchema,
    titre: nonEmptyText,
    conteneurs: z.array(conteneurSchema).min(2),
    camion: camionSchema,
    actuelle: tourneeActuelleSchema,
    /** Les jours où le camion peut sortir. */
    jours: z.array(nonEmptyText).min(1),
    /**
     * Ce qu'un conteneur débordé coûte à la ville.
     *
     * Le bilan montré à l'îlot ne compte que kilomètres, carburant et CO₂ : à
     * ce compte-là, ne rien collecter serait la meilleure stratégie. Le
     * débordement passe donc par la satisfaction des habitants — une
     * conséquence, pas un quatrième chiffre à lire.
     */
    effets_debordement: effectsSchema,
    /** Ce qu'une semaine bien organisée rapporte. */
    effets_reussite: effectsSchema,
    feedback_sans_debordement: nonEmptyText,
    feedback_avec_debordement: nonEmptyText,
  })
  .strict();
export type Tournee = z.infer<typeof tourneeSchema>;

export const engineRulesSchema = z
  .object({
    combinaison_inconnue: nonEmptyText,
    calcul_effets: nonEmptyText,
    budget_insuffisant: nonEmptyText,
    sauvegarde: nonEmptyText,
  })
  .strict();
export type EngineRules = z.infer<typeof engineRulesSchema>;

/**
 * Les phrases que le jeu dit à l'élève quand il refuse un geste.
 *
 * `regles_moteur` reste la documentation des règles, rédigée pour la personne
 * qui implémente le jeu. Ces messages-ci sont l'inverse : ils sont adressés à
 * un élève de 4e, et c'est la seule source autorisée (règle n° 2). Toutes les
 * clés sont obligatoires, pour qu'un message oublié fasse échouer
 * `validate:content` au build plutôt que d'apparaître devant une classe.
 *
 * N'y figurent que les refus qu'un geste d'élève peut produire. Les autres
 * codes d'erreur du moteur signalent un défaut de programmation : leur message
 * doit rester technique, parce que c'est un développeur qui le lira.
 */
export const playerMessagesSchema = z
  .object({
    budget_insuffisant: nonEmptyText,
    case_pleine: nonEmptyText,
    role_incompatible: nonEmptyText,
    equipement_deja_place: nonEmptyText,
    reseau_requis_pour_superviser: nonEmptyText,
  })
  .strict();
export type PlayerMessages = z.infer<typeof playerMessagesSchema>;

/**
 * Les réglages pédagogiques, ceux qui s'ajustent après une vraie séance.
 *
 * Règle non négociable n° 2 : les chiffres du jeu viennent du contenu. Ils
 * étaient des constantes du moteur, donc modifiables seulement par une
 * livraison ; ils sont ici pour que l'enseignant puisse les régler entre deux
 * classes. Les bornes ne sont pas décoratives : un podium à zéro place ou un
 * nombre de problèmes à repérer supérieur au nombre de problèmes existants
 * rendrait le chapitre 1 infranchissable.
 */
export const settingsSchema = z
  .object({
    /** Problèmes à repérer avant de pouvoir quitter l'exploration. */
    problemes_a_reperer_minimum: z.number().int().min(1).max(20),
    /** Places sur le podium des priorités, à la fin du chapitre 1. */
    places_au_podium: z.number().int().min(1).max(6),
    /** Essais supplémentaires accordés par problème mal classé. */
    secondes_chances: z.number().int().min(0).max(3),
    /** Retours à la conception autorisés après un déploiement. */
    retours_a_la_conception: z.number().int().min(0).max(3),
    /** Intensité de veille d'un lampadaire intelligent, en pourcentage. */
    niveau_veille_pourcent: z.number().int().min(0).max(100),
  })
  .strict();
export type Settings = z.infer<typeof settingsSchema>;

export const eventConditionSchema = z.union([
  z.object({ solutions: z.array(idSchema).min(1) }).strict(),
  z.object({ consommation_max: z.number() }).strict(),
]);
export type EventCondition = z.infer<typeof eventConditionSchema>;

export const eventBranchSchema = z.union([
  z
    .object({
      si_equipement_present: idSchema,
      effets: effectsSchema,
      message: nonEmptyText,
    })
    .strict(),
  z
    .object({
      sinon: z.literal(true),
      effets: effectsSchema,
      message: nonEmptyText,
    })
    .strict(),
]);
export type EventBranch = z.infer<typeof eventBranchSchema>;

export const eventChoiceSchema = z
  .object({
    id: idSchema,
    texte: nonEmptyText,
    effets: effectsSchema,
  })
  .strict();
export type EventChoice = z.infer<typeof eventChoiceSchema>;

export const gameEventSchema = z
  .object({
    id: idSchema,
    chapitre: chapterIdSchema,
    titre: nonEmptyText,
    texte: nonEmptyText,
    condition: eventConditionSchema.nullable(),
    effets_si: z.array(eventBranchSchema).min(2).optional(),
    choix: z.array(eventChoiceSchema).min(2).optional(),
    effets: effectsSchema.optional(),
    debat_classe: nonEmptyText.optional(),
  })
  .strict()
  .refine((e) => Boolean(e.effets_si ?? e.choix ?? e.effets), {
    message:
      'Un événement doit produire un résultat : « effets_si », « choix » ou « effets » est requis.',
  })
  .refine((e) => !e.effets_si || e.effets_si.some((b) => 'sinon' in b), {
    message: 'Un événement à branches doit prévoir une branche « sinon ».',
  });
export type GameEvent = z.infer<typeof gameEventSchema>;

export const programCompetenceSchema = z
  .object({
    id: idSchema,
    theme: idSchema,
    libelle: nonEmptyText,
  })
  .strict();
export type ProgramCompetence = z.infer<typeof programCompetenceSchema>;

export const programThemeSchema = z
  .object({
    id: idSchema,
    libelle: nonEmptyText,
  })
  .strict();
export type ProgramTheme = z.infer<typeof programThemeSchema>;

/**
 * Couverture du programme, chapitre par chapitre.
 *
 * `partialRecord` et non `record` : en Zod 4, un `record` dont la clé est une
 * énumération exige **toutes** les clés. Un chapitre de la séquence complète
 * peut ne pas encore être décrit ; ce sont les contrôles d'intégrité qui
 * imposent la présence des chapitres du MVP.
 */
export const sequenceCoverageSchema = z.partialRecord(anyChapterIdSchema, z.array(idSchema).min(1));
export type SequenceCoverage = z.infer<typeof sequenceCoverageSchema>;

/* ------------------------------------------------------------------ */
/* Contenu complet                                                     */
/* ------------------------------------------------------------------ */

export const contentSchema = z
  .object({
    meta: metaSchema,
    jauges: z.array(gaugeSchema).min(1),
    quartiers: z.array(districtSchema).min(1),
    categories_problemes: z.array(problemCategorySchema).min(1),
    chapitres: z.array(chapterSchema).min(1),
    problemes: z.array(problemSchema).min(1),
    equipements: z.array(equipmentSchema).min(1),
    solutions: z.array(solutionSchema).min(1),
    programmes: z.array(programmeSchema),
    meteo_semaine: z.array(journeeMeteoSchema),
    tournees: z.array(tourneeSchema),
    regles_moteur: engineRulesSchema,
    messages: playerMessagesSchema,
    reglages: settingsSchema,
    evenements: z.array(gameEventSchema),
    competences_programme: z.array(programCompetenceSchema).min(1),
    themes_programme: z.array(programThemeSchema).min(1),
    couverture_sequence: sequenceCoverageSchema,
  })
  .strict();

export type Content = z.infer<typeof contentSchema>;
